/**
 * mSTEM mock firmware catalog (DG-16). Keyed by device id — robot ids match
 * the `id` field in robots.js, SmartHome kit ids match smarthome-kits.js.
 * Every device has 3 versions ordered newest-first (index 0 = latest).
 *
 * Each entry: {version, releaseDate (ISO YYYY-MM-DD), sizeKB, releaseNotes}
 *
 * Used by FirmwareWizardModal Step 2 (GEN-05) to populate the version dropdown
 * and by KitDetailModal (SMH-04) to render the kit firmware history + the
 * "có bản mới / đã mới nhất" comparison. BE team will replace with a real
 * firmware registry API.
 */

const FIRMWARES = {
    'mbf-robot-v1': [
        {
            version: '1.0.3',
            releaseDate: '2026-05-20',
            sizeKB: 128,
            releaseNotes: 'Fix L298N motor jitter on Arduino UNO.'
        },
        {
            version: '1.0.2',
            releaseDate: '2026-03-15',
            sizeKB: 124,
            releaseNotes: 'Improved ultrasonic sensor stability.'
        },
        {
            version: '1.0.1',
            releaseDate: '2026-01-10',
            sizeKB: 120,
            releaseNotes: 'Initial public release.'
        }
    ],
    'mbf-smart-car-pro': [
        {
            version: '2.1.0',
            releaseDate: '2026-06-01',
            sizeKB: 384,
            releaseNotes: 'ESP32 BLE pairing reliability.'
        },
        {
            version: '2.0.5',
            releaseDate: '2026-04-22',
            sizeKB: 372,
            releaseNotes: 'Wi-Fi reconnect after deep sleep.'
        },
        {
            version: '2.0.0',
            releaseDate: '2026-02-14',
            sizeKB: 360,
            releaseNotes: 'Migrated to ESP-IDF 5.x.'
        }
    ],
    'mbf-mini-bot': [
        {
            version: '1.6.0',
            releaseDate: '2026-06-10',
            sizeKB: 96,
            releaseNotes: 'micro:bit V2 audio API support (latest — recommended upgrade).'
        },
        {
            version: '1.5.2',
            releaseDate: '2026-04-01',
            sizeKB: 92,
            releaseNotes: 'LED matrix scroll speed fix.'
        },
        {
            version: '1.5.0',
            releaseDate: '2026-02-20',
            sizeKB: 90,
            releaseNotes: 'Initial release for MBF Mini Bot.'
        }
    ],
    'mbf-ai-camera-bot': [
        {
            version: '1.0.0-beta',
            releaseDate: '2026-06-05',
            sizeKB: 512,
            releaseNotes: 'Beta — face detection mock API.'
        },
        {
            version: '0.9.0',
            releaseDate: '2026-05-12',
            sizeKB: 480,
            releaseNotes: 'Camera driver alpha.'
        },
        {
            version: '0.8.0',
            releaseDate: '2026-04-01',
            sizeKB: 440,
            releaseNotes: 'Initial ESP32-S3 bring-up.'
        }
    ],
    'mbf-lab-trainer': [
        {
            version: '3.0.1',
            releaseDate: '2026-05-28',
            sizeKB: 256,
            releaseNotes: 'Raspberry Pi Pico W Wi-Fi stack.'
        },
        {
            version: '3.0.0',
            releaseDate: '2026-04-15',
            sizeKB: 252,
            releaseNotes: 'Major rewrite for Pico 2.'
        },
        {
            version: '2.4.3',
            releaseDate: '2026-02-28',
            sizeKB: 224,
            releaseNotes: 'Legacy Pico 1 final release.'
        }
    ],
    // ── SMH-04: SmartHome kit firmware (keys match smarthome-kits.js ids) ──
    // Kits share the same {version, releaseDate, sizeKB, releaseNotes} shape so
    // KitDetailModal can reuse the wizard's Step-2 metadata rendering.
    'mbf-home-starter': [
        {
            version: '1.2.0',
            releaseDate: '2026-06-18',
            sizeKB: 168,
            releaseNotes: 'Ổn định đọc cảm biến DHT11 khi relay đóng/mở liên tục.'
        },
        {
            version: '1.1.0',
            releaseDate: '2026-04-09',
            sizeKB: 160,
            releaseNotes: 'Thêm hẹn giờ bật/tắt relay theo lịch.'
        },
        {
            version: '1.0.0',
            releaseDate: '2026-02-15',
            sizeKB: 152,
            releaseNotes: 'Phiên bản phát hành đầu tiên cho Home Starter Kit.'
        }
    ],
    'mbf-classroom-sensor-pack': [
        {
            version: '2.0.1',
            releaseDate: '2026-07-02',
            sizeKB: 244,
            releaseNotes: 'Sửa lỗi trôi giá trị cảm biến ánh sáng khi bật đèn huỳnh quang.'
        },
        {
            version: '2.0.0',
            releaseDate: '2026-05-20',
            sizeKB: 240,
            releaseNotes: 'Hỗ trợ 5 cảm biến đồng thời, đẩy dữ liệu 1 giây/lần.'
        },
        {
            version: '1.3.4',
            releaseDate: '2026-03-01',
            sizeKB: 210,
            releaseNotes: 'Bản cuối của dòng firmware 1.x.'
        }
    ],
    'mbf-security-demo': [
        {
            version: '1.5.0',
            releaseDate: '2026-07-08',
            sizeKB: 288,
            releaseNotes: 'Bổ sung nhật ký quẹt thẻ RFID và cảnh báo vượt ngưỡng.'
        },
        {
            version: '1.4.2',
            releaseDate: '2026-05-04',
            sizeKB: 276,
            releaseNotes: 'Tăng tốc độ đọc thẻ MFRC522.'
        },
        {
            version: '1.4.0',
            releaseDate: '2026-03-12',
            sizeKB: 270,
            releaseNotes: 'Hỗ trợ còi báo động và cảm biến cửa từ.'
        }
    ],
    'mbf-advanced-iot': [
        {
            version: '3.1.0',
            releaseDate: '2026-06-25',
            sizeKB: 512,
            releaseNotes: 'Kết nối MQTT ổn định sau khi mất điện, tự động kết nối lại.'
        },
        {
            version: '3.0.2',
            releaseDate: '2026-05-06',
            sizeKB: 500,
            releaseNotes: 'Sửa lỗi treo khi bật đồng thời 4 relay.'
        },
        {
            version: '3.0.0',
            releaseDate: '2026-04-02',
            sizeKB: 496,
            releaseNotes: 'Chuyển sang kiến trúc hai vi điều khiển ESP32-S3 + RP2040.'
        }
    ]
};

export default FIRMWARES;
